import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormBuilder, FormControlStatus, FormGroup, ValidationErrors } from '@angular/forms';
import {
    ArrayModel,
    assignModelOptions,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ModalClass,
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimesheetAddressModel,
    isEowEntry,
    isGapEntry,
    isManualEntry,
    isNormalEntry,
    isSowEntry,
    isTravelBackEntry,
    isTravelToEntry,
    TimesheetEntryInterface,
    TimesheetEntryModel,
    TimesheetEowModel,
    TimesheetGapModel,
    TimesheetManualEntryModel,
    TimesheetService,
    TimesheetSowModel,
    UiResponsiveService,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    ValueHelperModel,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faPause, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AddressSelectComponent } from '../address-select/address-select.component';
import { debounceTime, Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';

const DEBOUNCE_TIME_MS = 200;

@Component({
    selector: 'pace-timesheet',
    templateUrl: './timesheet.component.html',
    styleUrls: ['./timesheet.component.scss'],
    standalone: false,
})
export class TimesheetComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: ArrayModel<TimesheetEntryInterface>;

    @Input()
    public readonly: boolean;

    @Input()
    public parentForm: FormArray;

    @Input() public downloadInProgress = false;

    @Output()
    public autoSaveTimesheetForm = new EventEmitter<void>();

    public componentDestroyed = new Subject<void>();
    // internal variables
    public timesheetForm: FormGroup;
    public timesheetFormChildren: FormArray;
    public displayAddress = false;
    private areAddressesValid = false;
    public displayMeansOfTransport = false;

    public taskTypes: ValueHelperModel[];
    public workCodes: ValueHelperModel[];
    public readonly timeFormat: string;

    public faPause = faPause;
    public faTrashAlt = faTrashAlt;

    // type guard function
    public isGap: (entry: TimesheetEntryInterface) => entry is TimesheetGapModel;
    public isManual: (entry: TimesheetEntryInterface) => entry is TimesheetManualEntryModel;
    public isNormal: (entry: TimesheetEntryInterface) => entry is TimesheetEntryModel;
    public isSoW: (entry: TimesheetEntryInterface) => entry is TimesheetSowModel;
    public isEoW: (entry: TimesheetEntryInterface) => entry is TimesheetEowModel;
    public isTravelBack: (entry: TimesheetEntryInterface) => boolean;
    public isTravelTo: (entry: TimesheetEntryInterface) => boolean;

    public displayAddressOptions = false;
    public displayAdditionalTravelBack: boolean;
    public readonly enableStandbyTravel: boolean;

    private entriesWithoutSowAndEow: TimesheetEntryInterface[] = [];
    public isMobile$: Observable<boolean>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly modalService: BsModalService,
        private readonly formBuilder: FormBuilder,
        private readonly timesheetService: TimesheetService,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly uiResponsiveService: UiResponsiveService,
    ) {
        this.displayAddress = configService.getDisplayTimesheetAddress();
        this.displayMeansOfTransport = configService.getDisplayMeansOfTransport();
        this.taskTypes = [];
        this.taskTypes = this.valueHelperService.getTimesheetGapTypeInfo(); // contains both productive and non-productive entries
        this.workCodes = this.valueHelperService.getTimeRecordingTaskTypeInfo(); // contains productive entries

        // the following values and functions are set on the controller for easier access in the markup
        this.timeFormat = this.clientManager.getTimeFormat();
        this.isGap = isGapEntry.bind(this);
        this.isManual = isManualEntry.bind(this);
        this.isNormal = isNormalEntry.bind(this);
        this.isSoW = isSowEntry.bind(this);
        this.isEoW = isEowEntry.bind(this);
        this.isTravelBack = isTravelBackEntry.bind(this);
        this.isTravelTo = isTravelToEntry.bind(this);
        this.displayAddressOptions = configService.getDisplayTimesheetAddressOptions();
        this.enableStandbyTravel = configService.getEnableStandbyTravel();
        this.displayAdditionalTravelBack = configService.getDisplayAdditionalTravelBack();
    }

    public ngOnInit(): void {
        this.checkEntries();

        const timesheetValidator = (): ValidationErrors | null => this.checkValidity();
        this.timesheetFormChildren = this.formBuilder.array([]);
        this.timesheetForm = this.formBuilder.group(
            {
                children: this.timesheetFormChildren,
            },
            {
                validators: timesheetValidator,
            },
        );

        Promise.resolve(undefined)
            .then(() => {
                this.parentForm.push(this.timesheetForm);
            })
            .catch((error: Error) =>
                this.log.error(
                    'Error in TimesheetComponent -> ngOnInit -> parentForm.push: ',
                    JSON.stringify(error, Object.getOwnPropertyNames(error)),
                ),
            );
        this.isMobile$ = this.uiResponsiveService.isMobile$;

        this.timesheetForm.statusChanges
            .pipe(
                debounceTime(DEBOUNCE_TIME_MS),
                filter((status: FormControlStatus) => {
                    // Only run autosave when the changes are coming from the user and an autosave is wanted.
                    // With every auto-save a new TimesheetAndExpensesP58Model is being created and read into the forms, triggering this to fire again.
                    // To avoid this endless loop, we disallow autosave once after a new model is created.
                    const isUIUpdatedByUser = this.timesheetService.isAutoSaveAllowedForNextChange;
                    this.timesheetService.isAutoSaveAllowedForNextChange = true;
                    return (
                        isUIUpdatedByUser &&
                        (status === 'VALID' || (this.timesheetFormChildren.valid && this.areAddressesValid))
                    );
                }),
                debounceTime(DEBOUNCE_TIME_MS),
                tap(() => this.autoSaveTimesheetForm.emit()),
                takeUntil(this.componentDestroyed),
            )
            .subscribe();
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj.readonly && typeof changesObj.readonly.currentValue !== 'undefined') {
            this.readonly = <boolean>changesObj.readonly.currentValue;
        }
        if (changesObj.model && changesObj.model.currentValue) {
            this.model = <ArrayModel<TimesheetEntryInterface>>changesObj.model.currentValue;
            this.checkEntries();
        }
    }

    public ngOnDestroy(): void {
        this.componentDestroyed.next();
        this.componentDestroyed.complete();
    }

    /**
     * Search value helpers for a matching description for this work code entry.
     * we might need to search in two different value helpers to cover both manual and normal entries.
     *
     * @param entry the entry to search for
     * @returns the description of the entry
     */
    public getDesc(entry: TimesheetEntryModel): string {
        let taskType = this.taskTypes.find((type) => type.code === entry.workCode.value);

        if (!taskType) {
            taskType = this.workCodes.find((workCode) => workCode.code === entry.workCode.value);
            if (this.enableStandbyTravel) {
                // If stand by travel is enabled, the travel entries' descriptions depend on whether they are billable or not
                if (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
                    return <string>this.translate.instant('TIMESHEET_TRAVEL_TO');
                } else if (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY) {
                    return <string>this.translate.instant('TIMESHEET_TRAVEL_TO_STANDBY');
                }
            } else {
                if (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
                    return <string>this.translate.instant('TIMESHEET_TRAVEL_TO');
                } else if (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
                    return <string>this.translate.instant('TIMESHEET_TRAVEL_BACK');
                }
            }
        }

        return taskType ? taskType.descText : '';
    }

    public getEntryDurationInMinutes(entry: TimesheetEntryInterface): number {
        return moment.duration(entry.endDate.value.diff(entry.startDate.value)).asMinutes();
    }

    public showAddTravelBack(entry: TimesheetEntryInterface, index: number): boolean {
        if (this.enableStandbyTravel) {
            return this.isGap(entry) && !!this.getPreviousTravelBackEntryForIndex(entry.index);
        }
        return this.displayAdditionalTravelBack && index === this.model.value.length - 1 && this.isGap(entry);
    }

    public addAdditionalTravelBack(entry: TimesheetEntryInterface, index: number): void {
        const now = moment().startOf('minute');
        if (this.enableStandbyTravel) {
            const travelBackEntry = this.getPreviousTravelBackEntryForIndex(entry.index);
            if (!travelBackEntry) {
                this.log.warn(
                    `TimesheetComponent: addAdditionalTravelBack: could not find previous travel back entry for entry with index: ${entry.index}`,
                );
                return;
            }
            this.addManualEntry(index, travelBackEntry);
            this.checkEntries();
        } else {
            const lastTravelBackEntry = this.getLastTravelBackEntry();
            if (!lastTravelBackEntry) {
                this.log.warn(
                    'TimesheetComponent: addAdditionalTravelBack: could not find the last travel back entry for current timesheet',
                );
                return;
            }
            const manualEntry = new TimesheetManualEntryModel(
                lastTravelBackEntry.endTime.value.clone(),
                lastTravelBackEntry.endTime.value.clone(),
                lastTravelBackEntry.endTime.value.clone(),
                now.isBefore(lastTravelBackEntry.endTime.value) ? lastTravelBackEntry.endTime.value.clone() : now,
                undefined,
                false,
                false,
                lastTravelBackEntry.notificationId,
            );
            // replace the gap entry with a corresponding manual entry
            this.model.value.splice(index, 1, manualEntry);
        }
    }

    private getLastTravelBackEntry(): TimesheetEntryModel | undefined {
        const lastTravelBackEntry = _.findLast(
            this.model.value,
            (entry) =>
                entry instanceof TimesheetEntryModel && entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
        );
        return lastTravelBackEntry ? <TimesheetEntryModel>lastTravelBackEntry : undefined;
    }

    public addManualEntry(index: number, previousEntry?: TimesheetEntryModel): void {
        const gapEntry = this.model.value[index];
        const manualEntry = new TimesheetManualEntryModel(
            gapEntry.startTime.value,
            gapEntry.endTime.value,
            gapEntry.startTime.value,
            gapEntry.endTime.value,
            undefined,
            true,
            previousEntry?.isTravelBillable,
            previousEntry?.notificationId,
            previousEntry
                ? previousEntry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY
                    ? TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
                    : TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY
                : undefined,
        );
        // replace the gap entry with a corresponding manual entry
        this.model.value.splice(index, 1, manualEntry);
    }

    /**
     * The max value of End of work has to be updated wrt to current device time, and not the time when timesheet was opened
     *
     * @param {TimesheetEntryModel} entry the entry to update
     */
    public updateEoWMaxValue(entry: TimesheetEowModel): void {
        entry.updateEoWMaxValueOption();
    }

    public shouldShowRemoveEowGapButton(): boolean {
        const result = this.model.value.filter((entry: TimesheetEntryInterface) => {
            return isManualEntry(entry) || isNormalEntry(entry);
        });
        return result.length > 0;
    }

    /**
     * Sets the start and end date/time of the EoW entry equal to the last non-gap, non-sow and non-eow entry.
     * This to remove the last gap entry between a meaningful non-sow entry and EoW.
     * (background: user might finish work at 4PM but open timesheet at 5PM. This results in a gap from 4PM to 5PM
     * which needs to be removed manually by editing EoW. This function does that automatically.)
     *
     * @param entry the timesheet entry to remove the gap for
     */
    public removeEowGap(entry: TimesheetEntryInterface): void {
        let lastTimesheetEntryExceptEow = moment().startOf('minute');

        this.model.value.forEach((timesheetEntry) => {
            if (!isEowEntry(timesheetEntry) && !isGapEntry(timesheetEntry)) {
                lastTimesheetEntryExceptEow = timesheetEntry.endDate.value;
            }
        });
        entry.startDate.value.set(lastTimesheetEntryExceptEow.clone().toObject());
        entry.startTime.value.set(lastTimesheetEntryExceptEow.clone().toObject());
        entry.endDate.value.set(lastTimesheetEntryExceptEow.clone().toObject());
        entry.endTime.value.set(lastTimesheetEntryExceptEow.clone().toObject());

        // Don't allow auto-save after setting End of Work flush; This would read in a new timesheet and reset End of Work to moment();
        this.timesheetService.isAutoSaveAllowedForNextChange = false;
        this.checkEntries();
    }

    private getPreviousTravelBackEntryForIndex(entryIndex: number): TimesheetEntryModel | undefined {
        // Checks if previous entry is travel-back, and return it
        const index = this.entriesWithoutSowAndEow.findIndex((entry) => entry.index === entryIndex);
        return this.isTravelBack(this.entriesWithoutSowAndEow[index - 1])
            ? <TimesheetEntryModel>this.entriesWithoutSowAndEow[index - 1]
            : undefined;
    }

    public checkEntries(): void {
        this.model.value = this.timesheetService.sortEntries(this.model.value);
        this.model.value = this.timesheetService.closeGaps(this.model.value);

        if (this.enableStandbyTravel) {
            this.entriesWithoutSowAndEow = this.model.value.filter((entry) => !this.isSoW(entry) && !this.isEoW(entry));
            this.model.value.forEach((entry) => {
                const previousTravelBackEntry = this.getPreviousTravelBackEntryForIndex(entry.index);
                if (typeof previousTravelBackEntry?.travelBackDropdown !== 'undefined') {
                    previousTravelBackEntry.disableTravelBackEntry(
                        this.readonly || (this.isManual(entry) && !!entry.travelType),
                    );
                    if (this.isManual(entry) && !this.readonly) {
                        entry.startDate = assignModelOptions(
                            entry.startDate,
                            {
                                ...entry.startDate.options,
                                readonly: !!entry.travelType,
                            },
                            entry,
                        );
                        entry.startTime = assignModelOptions(
                            entry.startTime,
                            {
                                ...entry.startTime.options,
                                readonly: !!entry.travelType,
                            },
                            entry,
                        );
                    }
                }
            });
        }

        if (this.timesheetForm) {
            this.timesheetForm.updateValueAndValidity();
        }
    }

    public deleteEntry(index: number): void {
        this.model.value.splice(index, 1);
        this.checkEntries();
    }

    public launchSelectAddressModal(entry: TimesheetManualEntryModel): void {
        const modalInstance = this.modalService.show(AddressSelectComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                preselectedAddress: entry.address,
                timesheetEntries: this.model,
            },
            ariaLabelledBy: 'address-select-modal-title',
        });

        modalInstance.content?.timeSheetAddressAdded.subscribe((address: TimesheetAddressModel) => {
            entry.address = address;
            if (this.timesheetForm) {
                this.timesheetForm.updateValueAndValidity();
            }
        });
    }

    private checkValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};
        this.areAddressesValid = true;

        // Check if all non-productive entries have an address set
        const areManualEntriesValid = this.displayAddress
            ? this.timesheetService.checkManualEntryAddresses(this.model.value)
            : true;
        if (!areManualEntriesValid) {
            errors['manual-entries-invalid'] = 'Manual entry with invalid address found';
            this.areAddressesValid = false;
        }

        // check if there are gaps left in the timesheet
        const noGapEntriesLeft = this.timesheetService.checkForUnfilledGaps(this.model.value);
        if (!noGapEntriesLeft) {
            errors['gap-entries-found'] = 'There are gaps in the timesheet';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
